import { useState, useMemo } from 'react'
import { calculateTAPS, getThreatContextSummary } from '../../../shared/utils/threatScoring'
import threatProfiles from '../../../shared/data/threatProfiles.json'

/**
 * Renders Threat Context Summary and TAPS Priority Table cards.
 * Used on both the Threat Context page (under Risk Intelligence) and optionally Threat Analysis.
 */
function ThreatContextCards({ assessment, gapAnalysis, scores }) {
  const [sortBy, setSortBy] = useState('tapsScore')
  const [sortDirection, setSortDirection] = useState('desc')

  const selectedProfile = useMemo(() => {
    const profileKey = assessment?.responses?.threatContext?.profileKey
    return profileKey ? threatProfiles.profiles.find(p => p.verticalKey === profileKey) : null
  }, [assessment?.responses?.threatContext])

  const tapsResults = useMemo(() => {
    if (!gapAnalysis || !selectedProfile) return null
    const threatContext = {
      threat_actor_weights: assessment?.responses?.threatContext?.actorWeights || {},
      attack_vector_weights: assessment?.responses?.threatContext?.vectorWeights || {},
      target_asset_weights: assessment?.responses?.threatContext?.assetWeights || {}
    }
    const impactData = assessment?.responses?.impactData || {}
    return calculateTAPS(gapAnalysis, threatContext, impactData, selectedProfile)
  }, [gapAnalysis, selectedProfile, assessment?.responses])

  const threatSummary = useMemo(() => {
    if (!selectedProfile) return null
    return getThreatContextSummary(selectedProfile, assessment?.responses?.threatContext)
  }, [selectedProfile, assessment?.responses?.threatContext])

  const sortedCategories = useMemo(() => {
    if (!tapsResults?.categories) return []
    return [...tapsResults.categories].sort((a, b) => {
      const aVal = a[sortBy] || 0
      const bVal = b[sortBy] || 0
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [tapsResults, sortBy, sortDirection])

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(field)
      setSortDirection('desc')
    }
  }

  const getPriorityColors = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
      case 'high': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200'
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
      case 'low': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
      default: return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
    }
  }

  if (!selectedProfile) return null

  return (
    <div className="space-y-8">
      {/* Threat Context Summary */}
      {threatSummary && (
        <div className="card p-6">
          <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Threat Context Summary</h3>
          </div>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Industry Profile:</span>
              <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-accent-200 rounded-full text-sm font-medium">
                {threatSummary.profile.name}
              </span>
            </div>
            {threatSummary.hasCustomizations && (
              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded-full text-xs">
                Custom Weights Applied
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Top Threat Actors
              </h4>
              <ul className="space-y-2">
                {threatSummary.topThreatActors.map((actor) => (
                  <li key={actor.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{actor.name}</span>
                    <span className="text-red-600 dark:text-red-400 font-medium">{Math.round(actor.adjustedLikelihood * 100)}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Top Attack Vectors
              </h4>
              <ul className="space-y-2">
                {threatSummary.topAttackVectors.map((vector) => (
                  <li key={vector.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{vector.name}</span>
                    <span className="text-orange-600 dark:text-orange-400 font-medium">{Math.round(vector.adjustedPrevalence * 100)}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Regulatory Context
              </h4>
              <div className="flex flex-wrap gap-1 mb-2">
                {threatSummary.regulatoryContext.frameworks.slice(0, 3).map((framework, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded text-xs">
                    {framework}
                  </span>
                ))}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <p>Max penalty: ${threatSummary.regulatoryContext.maxPenalty.toLocaleString()}</p>
                <p>Breach notification: {threatSummary.regulatoryContext.breachNotificationDays} days</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAPS Priority Table */}
      {tapsResults && tapsResults.categories.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Threat-Adjusted Priority Score (TAPS)
                </h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Prioritized security gaps based on threat relevance and business impact
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600 dark:text-accent-300">
                  {tapsResults.summary.averageTAPS}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Average TAPS</div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rank</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Domain</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('gapScore')}>
                    Gap Score {sortBy === 'gapScore' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('threatRelevance')}>
                    Threat Relevance {sortBy === 'threatRelevance' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('impactWeight')}>
                    Impact {sortBy === 'impactWeight' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('tapsScore')}>
                    TAPS {sortBy === 'tapsScore' && (sortDirection === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedCategories.map((cat) => (
                  <tr key={cat.domain} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">{cat.rank}</span>
                    </td>
                    <td className="px-4 py-3"><div className="font-medium text-gray-900 dark:text-white">{cat.domain}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`font-medium ${cat.gapScore < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{cat.gapScore}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                          <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${Math.min(100, cat.threatRelevance * 100)}%` }} />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">{cat.threatRelevance}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{cat.impactWeight}x</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-lg font-bold text-primary-600 dark:text-accent-300">{cat.tapsScore}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColors(cat.priorityLevel)}`}>{cat.priorityLevel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default ThreatContextCards
